/**
 * Vendor Portal Dashboard
 * For vendors to submit invoices with file attachments, track status, and manage their profile
 */
import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  FileText, Plus, Upload, LogOut, Loader2, Paperclip, X, Download, Eye, File
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VendorPortal = () => {
  const [vendor, setVendor] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSubmit, setShowSubmit] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const [invoiceForm, setInvoiceForm] = useState({
    invoice_number: '', description: '', category: 'maintenance',
    line_items: [{ description: '', quantity: 1, unit_price: 0 }],
    notes: '', due_date: ''
  });

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('vendor_token')}` });

  useEffect(() => {
    fetchProfile();
    fetchInvoices();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API}/vendor/profile`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setVendor(data.vendor);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const fetchInvoices = async () => {
    try {
      const res = await fetch(`${API}/vendor/invoices`, { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setInvoices(data.invoices || []);
      }
    } catch (e) { console.error(e); }
  };

  const addLineItem = () => {
    setInvoiceForm({
      ...invoiceForm,
      line_items: [...invoiceForm.line_items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const updateLineItem = (index, field, value) => {
    const items = [...invoiceForm.line_items];
    items[index][field] = field === 'quantity' || field === 'unit_price' ? parseFloat(value) || 0 : value;
    setInvoiceForm({ ...invoiceForm, line_items: items });
  };

  const removeLineItem = (index) => {
    if (invoiceForm.line_items.length <= 1) return;
    setInvoiceForm({ ...invoiceForm, line_items: invoiceForm.line_items.filter((_, i) => i !== index) });
  };

  const calcTotal = () => invoiceForm.line_items.reduce((s, i) => s + (i.quantity * i.unit_price), 0);

  // File upload handler
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);
    const token = localStorage.getItem('vendor_token');

    for (const file of files) {
      if (file.size > 25 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 25MB limit`);
        continue;
      }

      try {
        const formData = new FormData();
        formData.append('file', file);

        const res = await fetch(`${API}/vendor/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData
        });

        if (res.ok) {
          const data = await res.json();
          setAttachedFiles(prev => [...prev, {
            file_id: data.file.id,
            filename: data.file.filename,
            storage_path: data.file.storage_path,
            content_type: data.file.content_type,
            size: data.file.size
          }]);
          toast.success(`${file.name} uploaded`);
        } else {
          const err = await res.json();
          toast.error(err.detail || `Failed to upload ${file.name}`);
        }
      } catch (err) {
        toast.error(`Upload error: ${file.name}`);
      }
    }
    setUploadingFiles(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachment = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const downloadFile = async (fileId, filename) => {
    try {
      const res = await fetch(`${API}/vendor/files/${fileId}/download`, {
        headers: getHeaders()
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        toast.error('Download failed');
      }
    } catch (e) {
      toast.error('Download error');
    }
  };

  const submitInvoice = async () => {
    setSubmitting(true);
    try {
      const subtotal = calcTotal();
      const tax = subtotal * 0;
      const res = await fetch(`${API}/vendor/invoices`, {
        method: 'POST',
        headers: { ...getHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...invoiceForm,
          subtotal,
          tax,
          total: subtotal + tax,
          attachments: attachedFiles.map(f => ({
            file_id: f.file_id,
            filename: f.filename,
            storage_path: f.storage_path
          }))
        })
      });
      if (res.ok) {
        toast.success('Invoice submitted successfully');
        setShowSubmit(false);
        setInvoiceForm({
          invoice_number: '', description: '', category: 'maintenance',
          line_items: [{ description: '', quantity: 1, unit_price: 0 }], notes: '', due_date: ''
        });
        setAttachedFiles([]);
        fetchInvoices();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Submission failed');
      }
    } catch (e) {
      toast.error('Failed to submit invoice');
    }
    setSubmitting(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('vendor_token');
    window.location.href = '/vendor/login';
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);
  const formatFileSize = (bytes) => bytes > 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;

  const statusBadge = (s) => {
    const m = {
      submitted: 'bg-blue-100 text-blue-800', under_review: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800',
      paid: 'bg-emerald-100 text-emerald-800'
    };
    return <Badge className={`${m[s] || 'bg-gray-100'} text-xs`}>{s?.replace('_', ' ')}</Badge>;
  };

  if (loading) return <div className="flex justify-center items-center min-h-screen"><Loader2 className="h-8 w-8 animate-spin text-emerald-500" /></div>;

  const pendingCount = invoices.filter(i => i.status === 'submitted' || i.status === 'under_review').length;
  const approvedCount = invoices.filter(i => i.status === 'approved').length;
  const paidTotal = invoices.filter(i => i.status === 'paid').reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="vendor-portal">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-900">Vendor Portal</h1>
            <p className="text-xs text-gray-500">{vendor?.company_name || vendor?.contact_name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => setShowSubmit(true)} data-testid="submit-invoice-btn">
              <Plus className="h-4 w-4 mr-1" /> Submit Invoice
            </Button>
            <Button size="sm" variant="ghost" onClick={handleLogout} data-testid="vendor-logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-4 space-y-4">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3"><p className="text-[10px] text-blue-600 font-medium">Total Invoices</p><p className="text-lg font-bold text-blue-700" data-testid="total-invoices">{invoices.length}</p></CardContent>
          </Card>
          <Card className="bg-yellow-50 border-yellow-200">
            <CardContent className="p-3"><p className="text-[10px] text-yellow-600 font-medium">Pending Review</p><p className="text-lg font-bold text-yellow-700" data-testid="pending-invoices">{pendingCount}</p></CardContent>
          </Card>
          <Card className="bg-green-50 border-green-200">
            <CardContent className="p-3"><p className="text-[10px] text-green-600 font-medium">Approved</p><p className="text-lg font-bold text-green-700" data-testid="approved-invoices">{approvedCount}</p></CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-3"><p className="text-[10px] text-emerald-600 font-medium">Paid Total</p><p className="text-lg font-bold text-emerald-700" data-testid="paid-total">{formatCurrency(paidTotal)}</p></CardContent>
          </Card>
        </div>

        {/* Invoice List */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center"><FileText className="h-4 w-4 mr-2 text-blue-600" />My Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            {invoices.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                <p className="text-sm text-gray-500">No invoices submitted yet</p>
                <Button size="sm" className="mt-3" onClick={() => setShowSubmit(true)}>Submit Your First Invoice</Button>
              </div>
            ) : (
              <div className="space-y-2">
                {invoices.map(inv => (
                  <div key={inv.id} className="p-3 border rounded-lg hover:bg-gray-50 transition-colors" data-testid={`invoice-${inv.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs font-medium">{inv.invoice_number}</span>
                          {statusBadge(inv.status)}
                        </div>
                        <p className="text-sm text-gray-700 mt-0.5 truncate">{inv.description || 'No description'}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Submitted: {new Date(inv.submitted_at).toLocaleDateString()}
                          {inv.due_date && ` | Due: ${inv.due_date}`}
                        </p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-bold text-sm">{formatCurrency(inv.total)}</p>
                        <p className="text-xs text-gray-500 capitalize">{inv.category}</p>
                      </div>
                    </div>

                    {/* Attachments */}
                    {inv.attachments && inv.attachments.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <Paperclip className="h-3 w-3 text-gray-400" />
                        {inv.attachments.map((att, ai) => (
                          <button
                            key={ai}
                            onClick={() => downloadFile(att.file_id, att.filename)}
                            className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] hover:bg-blue-100 transition-colors"
                            data-testid={`attachment-${att.file_id}`}
                          >
                            <File className="h-2.5 w-2.5" />
                            {att.filename}
                            <Download className="h-2.5 w-2.5" />
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Approval Chain */}
                    {inv.approval_chain && (
                      <div className="flex items-center gap-1 mt-2 text-[10px]">
                        {inv.approval_chain.map((step, si) => (
                          <React.Fragment key={si}>
                            <span className={`px-2 py-0.5 rounded ${step.status === 'approved' ? 'bg-green-100 text-green-700' : step.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}>
                              {step.role === 'property_manager' ? 'PM Review' : 'Board Approval'}
                              {step.status === 'approved' && ' \u2713'}
                              {step.status === 'rejected' && ' \u2717'}
                            </span>
                            {si < inv.approval_chain.length - 1 && <span className="text-gray-300">\u2192</span>}
                          </React.Fragment>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Submit Invoice Dialog */}
      <Dialog open={showSubmit} onOpenChange={setShowSubmit}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center"><Upload className="h-4 w-4 mr-2 text-blue-600" />Submit Invoice</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Invoice Number</Label><Input value={invoiceForm.invoice_number} onChange={e => setInvoiceForm({...invoiceForm, invoice_number: e.target.value})} placeholder="INV-001" data-testid="invoice-number-input" /></div>
              <div><Label className="text-xs">Category</Label>
                <Select value={invoiceForm.category} onValueChange={v => setInvoiceForm({...invoiceForm, category: v})}>
                  <SelectTrigger data-testid="invoice-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="repair">Repair</SelectItem>
                    <SelectItem value="renovation">Renovation</SelectItem>
                    <SelectItem value="landscaping">Landscaping</SelectItem>
                    <SelectItem value="plumbing">Plumbing</SelectItem>
                    <SelectItem value="electrical">Electrical</SelectItem>
                    <SelectItem value="roofing">Roofing</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Description</Label><Input value={invoiceForm.description} onChange={e => setInvoiceForm({...invoiceForm, description: e.target.value})} placeholder="Work performed..." data-testid="invoice-description-input" /></div>
            <div><Label className="text-xs">Due Date</Label><Input type="date" value={invoiceForm.due_date} onChange={e => setInvoiceForm({...invoiceForm, due_date: e.target.value})} data-testid="invoice-due-date-input" /></div>

            {/* Line Items */}
            <div>
              <Label className="text-xs font-medium">Line Items</Label>
              <div className="space-y-2 mt-1">
                {invoiceForm.line_items.map((item, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1 items-end">
                    <div className="col-span-6"><Input className="text-xs" placeholder="Description" value={item.description} onChange={e => updateLineItem(i, 'description', e.target.value)} data-testid={`line-item-desc-${i}`} /></div>
                    <div className="col-span-2"><Input className="text-xs" type="number" placeholder="Qty" value={item.quantity} onChange={e => updateLineItem(i, 'quantity', e.target.value)} data-testid={`line-item-qty-${i}`} /></div>
                    <div className="col-span-3"><Input className="text-xs" type="number" step="0.01" placeholder="Price" value={item.unit_price} onChange={e => updateLineItem(i, 'unit_price', e.target.value)} data-testid={`line-item-price-${i}`} /></div>
                    <div className="col-span-1">
                      {invoiceForm.line_items.length > 1 && (
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400" onClick={() => removeLineItem(i)}>\u00d7</Button>
                      )}
                    </div>
                  </div>
                ))}
                <Button size="sm" variant="outline" className="text-xs" onClick={addLineItem}><Plus className="h-3 w-3 mr-1" /> Add Line</Button>
              </div>
            </div>

            {/* Total */}
            <div className="flex justify-between items-center p-2 bg-gray-50 rounded font-medium text-sm">
              <span>Total</span>
              <span className="text-lg" data-testid="invoice-total">{formatCurrency(calcTotal())}</span>
            </div>

            {/* File Attachments */}
            <div>
              <Label className="text-xs font-medium flex items-center gap-1">
                <Paperclip className="h-3 w-3" /> Attachments (PDF, Images, Documents)
              </Label>
              <div className="mt-1.5 space-y-2">
                {/* Attached files list */}
                {attachedFiles.length > 0 && (
                  <div className="space-y-1">
                    {attachedFiles.map((f, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-blue-50 border border-blue-200 rounded text-xs" data-testid={`attached-file-${i}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <File className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                          <span className="truncate font-medium">{f.filename}</span>
                          <span className="text-gray-500 shrink-0">{formatFileSize(f.size)}</span>
                        </div>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-600 shrink-0" onClick={() => removeAttachment(i)}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="file-upload-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full border-dashed text-xs"
                  disabled={uploadingFiles}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-file-btn"
                >
                  {uploadingFiles ? (
                    <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Uploading...</>
                  ) : (
                    <><Upload className="h-3 w-3 mr-1" /> Attach Files (PDF, Images, etc.)</>
                  )}
                </Button>
              </div>
            </div>

            <div><Label className="text-xs">Notes</Label><textarea className="w-full border rounded p-2 text-sm min-h-[40px]" value={invoiceForm.notes} onChange={e => setInvoiceForm({...invoiceForm, notes: e.target.value})} placeholder="Additional notes..." data-testid="invoice-notes-input" /></div>
          </div>
          <DialogFooter>
            <Button size="sm" variant="outline" onClick={() => { setShowSubmit(false); setAttachedFiles([]); }} data-testid="invoice-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={submitInvoice} disabled={submitting || !invoiceForm.description} data-testid="invoice-submit-btn">
              {submitting ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Submitting...</> : 'Submit Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorPortal;
